/**
 * Keep / clip AI-delineated polygons so only the portion inside a parent farm is saved.
 */
import polygonClipping from 'polygon-clipping';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import { getArea } from 'ol/sphere';
import {
  getPolygonCentroid,
  getPolygonInteriorLonLat,
} from '../components/DrawingUtils';

type Ring = number[][];
type Poly = Ring[];
type Multi = Poly[];

function toClippingCoords(geometry: {
  type: string;
  coordinates: any;
}): Multi | null {
  if (!geometry?.coordinates) return null;
  if (geometry.type === 'Polygon') {
    return [geometry.coordinates as Poly];
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates as Multi;
  }
  return null;
}

function fromClippingResult(result: Multi): { type: string; coordinates: any } | null {
  if (!result?.length) return null;
  if (result.length === 1) {
    return { type: 'Polygon', coordinates: result[0] };
  }
  return { type: 'MultiPolygon', coordinates: result };
}

function geomAreaM2(geometry: { type: string; coordinates: any }): number {
  try {
    const format = new GeoJSON();
    const feat = format.readFeature(
      { type: 'Feature', geometry },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' }
    );
    const g = feat.getGeometry() as Geometry | undefined;
    if (!g) return 0;
    return getArea(g, { projection: 'EPSG:4326' });
  } catch {
    return 0;
  }
}

/**
 * Point-in-polygon via OpenLayers (EPSG:4326 lon/lat geometry).
 */
function isLonLatInsideParent(
  lonLat: [number, number],
  parentGeom4326: Geometry
): boolean {
  return parentGeom4326.intersectsCoordinate(lonLat);
}

/**
 * Clip each GeoJSON feature to the parent farm polygon.
 * Returns only non-empty intersections (portion strictly inside the parent).
 * Tiny slivers (< minAreaRatio of original, and < minAreaM2) are dropped.
 */
export function clipFeaturesToParentPolygon(
  features: Array<{ type?: string; geometry?: any; properties?: any }>,
  parentGeometry: { type: string; coordinates: any },
  options?: { minAreaM2?: number; minOverlapRatio?: number }
): Array<{ type: string; geometry: { type: string; coordinates: any }; properties?: any }> {
  const minAreaM2 = options?.minAreaM2 ?? 50; // ~0.005 ha
  const minOverlapRatio = options?.minOverlapRatio ?? 0.15;

  const parentCoords = toClippingCoords(parentGeometry);
  if (!parentCoords) return [];

  const format = new GeoJSON();
  const parentFeat = format.readFeature(
    { type: 'Feature', geometry: parentGeometry },
    { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' }
  );
  const parentGeom4326 = parentFeat.getGeometry();
  if (!parentGeom4326) return [];

  const out: Array<{
    type: string;
    geometry: { type: string; coordinates: any };
    properties?: any;
  }> = [];

  for (const feat of features) {
    const g = feat?.geometry;
    if (!g) continue;

    const childCoords = toClippingCoords(g);
    if (!childCoords) continue;

    let intersection: Multi;
    try {
      intersection = polygonClipping.intersection(parentCoords, childCoords) as Multi;
    } catch {
      continue;
    }

    const clipped = fromClippingResult(intersection);
    if (!clipped) continue;

    const originalArea = geomAreaM2(g);
    const clippedArea = geomAreaM2(clipped);
    if (clippedArea < minAreaM2) continue;
    if (originalArea > 0 && clippedArea / originalArea < minOverlapRatio) {
      // Mostly outside — skip
      continue;
    }

    // Require clipped interior to still lie inside parent
    const clippedInterior =
      getPolygonInteriorLonLat({ geometry: clipped }) ||
      getPolygonCentroid({ geometry: clipped });
    if (!clippedInterior || !isLonLatInsideParent(clippedInterior, parentGeom4326)) {
      continue;
    }

    out.push({
      type: 'Feature',
      geometry: clipped,
      properties: feat.properties ?? {},
    });
  }

  return out;
}
